import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import StatsToBadges from '@/atoms/Badge/StatsToBadges';
import getFactionIconName from '@/atoms/FactionIcon/utils';
import TextualData, { TEXTUAL_DATA_VARIANT } from '@/molecules/TextualData';
import Tile from '@/molecules/Tile';
import { CARD_ELEMENT_API_REF, CARD_TYPE_API_REF } from 'types/modifiers/references';
import formatAlteredIconsText from 'utils/formatAlteredIconsText';
import { CardDetailsWithStats } from 'hooks/queries/cards/useCardsDetailsApi';
import { useCardHistory } from 'hooks/queries/cards';
import CardBadge from '@/atoms/CardBadge';
import getBadgePropsFromHistory from '../CardHistory/HistoryToIcon';
import { useDateFnsWithLocale } from 'hooks/i18n';
import { LONG_DAY_LOCALE_OPTIONS } from 'hooks/i18n/IntlFormatOptions';

const CardMainTile = ({ card, className }: { card: CardDetailsWithStats; className?: string }) => {
  const { t } = useTranslation();
  const {
    queryResult: { data },
  } = useCardHistory({ cardRef: card.reference! });
  const { localeFormat } = useDateFnsWithLocale();

  const lastHistory = data?.data.news;

  return (
    <Tile className={className} icon={getFactionIconName(card.mainFaction?.reference)} title={card.name} withDivider>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-4">
          <TextualData label={t('type')} variant={TEXTUAL_DATA_VARIANT.smallInfo}>
            <Link className="text-system-info-400" href={`/cards?cardType[]=${card.cardType?.reference}`}>
              {card.cardType?.name}
            </Link>
          </TextualData>
          <TextualData label={t('subtype')} variant={TEXTUAL_DATA_VARIANT.smallInfo}>
            <ul className="flex flex-row">
              {card.cardSubTypes?.map((subType) => (
                <li key={subType.id} className="group">
                  <span className="group-first:hidden mr-1">,</span>
                  <Link className="text-system-info-400" href={`/cards?cardSubTypes[]=${subType.reference}`}>
                    {subType.name}
                  </Link>
                </li>
              ))}
            </ul>
          </TextualData>
          {card.cardType?.reference !== CARD_TYPE_API_REF.HERO && (
            <TextualData label={t('attributes')} variant={TEXTUAL_DATA_VARIANT.smallInfo}>
              <StatsToBadges stats={card.elements} />
            </TextualData>
          )}
          {lastHistory && lastHistory.fromDate && (
            <TextualData label={t('news')} variant={TEXTUAL_DATA_VARIANT.smallInfo}>
              <div className="flex gap-2  ">
                <CardBadge {...getBadgePropsFromHistory(lastHistory.type)} />
                <Link className="text-neutral-700 underline  " href="#history">
                  {t(`cardHistory.type.${lastHistory?.type}`)}{' '}
                  {t('cardHistory.news.from', {
                    date: localeFormat(new Date(lastHistory.fromDate), LONG_DAY_LOCALE_OPTIONS),
                  })}
                </Link>
              </div>
            </TextualData>
          )}
        </div>
        {card?.elements?.[CARD_ELEMENT_API_REF.MAIN_EFFECT] && (
          <TextualData label={t('main-effect')} variant={TEXTUAL_DATA_VARIANT.smallInfo}>
            {card.elements && formatAlteredIconsText(card.elements[CARD_ELEMENT_API_REF.MAIN_EFFECT])}
          </TextualData>
        )}
        {card?.elements?.[CARD_ELEMENT_API_REF.ECHO_EFFECT] && (
          <TextualData label={t('echo-effect')} variant={TEXTUAL_DATA_VARIANT.smallInfo}>
            {card.elements && formatAlteredIconsText(card.elements[CARD_ELEMENT_API_REF.ECHO_EFFECT])}
          </TextualData>
        )}
      </div>
    </Tile>
  );
};

export default CardMainTile;
